<script setup lang="ts">
import CoinThrower from "@/views/Calculators/ICzinPrediction/components/CoinThrower.vue";
import ICzinFAQ from "@/views/Calculators/ICzinPrediction/components/ICzinFAQ.vue";
import PredictionResult from "@/views/Calculators/ICzinPrediction/components/PredictionResult.vue";
import { ref, onMounted } from "vue";
import { fetchICzinPredictions } from "@/api/calculators";
import { ICzinCoinState, ICzinResponse } from "@/types/calculators";
import BackButton from "@/components/buttons/BackButton.vue";
import { ROUTE_NAMES } from "@/constants/routeNames";
import { useSafeBack } from "@/utils/useSafeBack";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const { safeBack } = useSafeBack();

const coins = ref<ICzinCoinState[]>([]);
const isFlipping = ref(false);
const flipResults = ref<number[]>([]);
const prediction = ref<ICzinResponse | null>(null);
const loadingApi = ref(false);

onMounted(() => {
  resetCoins();
});

function resetCoins() {
  coins.value = Array.from({ length: 3 }, () => {
    const side = randomSide();
    return {
      side,
      targetSide: side,
      angle: side === "eagle" ? 0 : 180,
    };
  });
  flipResults.value = [];
  loadingApi.value = false;
  prediction.value = null;
}

function randomSide() {
  return Math.random() < 0.5 ? "eagle" : "tails";
}

function flipAllCoins() {
  isFlipping.value = true;

  coins.value.forEach((coin, idx) => {
    coin.targetSide = randomSide();

    const isSame = coin.side === coin.targetSide;
    const rotations = isSame ? 2 : 3;

    const startAngle = coin.angle;
    const endAngle = startAngle + 180 * rotations;

    setTimeout(() => {
      coin.angle = endAngle;
      setTimeout(() => {
        coin.side = coin.targetSide;
        if (idx === coins.value.length - 1) {
          countResults();
          isFlipping.value = false;
        }
      }, 800);
    }, 100);
  });
}

async function countResults() {
  // 2 за орла, 3 за решку
  const sum = coins.value.reduce(
    (acc, coin) => acc + (coin.side === "eagle" ? 2 : 3),
    0
  );
  flipResults.value.push(sum);
  if (flipResults.value.length >= 6) {
    loadingApi.value = true;
    try {
      prediction.value = await fetchICzinPredictions(flipResults.value);
    } finally {
      loadingApi.value = false;
    }
  }
}

function handleBack() {
  if (prediction.value) {
    resetCoins();
    return;
  }
  safeBack(ROUTE_NAMES.TAROT);
}
</script>

<template>
  <div
    class="flex flex-col gap-6 h-full xl:h-screen w-full pt-6 xl:pt-[88px] pb-6 px-4"
  >
    <div class="relative items-center md:hidden px-8">
      <div class="absolute left-0">
        <BackButton @clicked="handleBack" />
      </div>
      <h2 class="text-2xl font-medium text-brown">
        {{ t("calculators.iczin.title") }}
      </h2>
    </div>
    <div
      class="text-left md:mx-auto py-6 px-4 md:px-10 bg-white rounded-[20px] flex flex-col items-center w-full md:max-w-[812px] h-full min-h-0 overflow-y-auto no-scrollbar"
    >
      <div
        class="relative hidden md:flex justify-center items-center w-full min-h-11 mb-10"
      >
        <div class="absolute left-0">
          <BackButton @clicked="handleBack" />
        </div>
        <h2 class="text-2xl font-semibold text-brown">
          {{ t("calculators.iczin.title") }}
        </h2>
      </div>
      <CoinThrower
        v-if="!prediction"
        :coins="coins"
        :results="flipResults"
        :is-flipping="isFlipping"
        :is-loading="loadingApi"
        @flip="flipAllCoins"
      />
      <ICzinFAQ v-if="!prediction" />
      <PredictionResult
        v-else
        :results="flipResults"
        :prediction="prediction"
        @reset="resetCoins"
      />
    </div>
  </div>
</template>
